import {useDebouncedCallback} from '@react-hookz/web';
import {updateStyleGuide} from 'Common/api';
import Button from 'Components/Tailwind/Button';
import {Checkbox} from 'Components/Tailwind/Checkbox';
import {Input} from 'Components/Tailwind/Input';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import React, {useCallback, useState} from 'react';
import {Project} from 'rendition-core';
import {ERROR, useAppStore} from 'stores/app';
import {defaultStyleGuide} from './constants';
import {StyleGuide} from './hooks/useStyleguideData';

interface Props {
    remove: () => void;
    pasteFromClipboard: () => void;
    value: StyleGuide | undefined;
    clipboardEnabled: boolean;
    setProject: React.Dispatch<React.SetStateAction<Project | undefined>>;
    setStyleGuide: React.Dispatch<React.SetStateAction<StyleGuide>>;
    previewText: string;
    setPreviewText: React.Dispatch<React.SetStateAction<string>>;
    showPreviewText: boolean;
    setShowPreviewText: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Header: React.FC<Props> = ({
    pasteFromClipboard,
    clipboardEnabled,
    remove,
    value,
    setProject,
    setStyleGuide,
    previewText,
    setPreviewText,
    showPreviewText,
    setShowPreviewText,
}) => {
    const setToast = useAppStore.use.setToast();
    const [saving, setSaving] = useState(false);
    const {userId} = useUserContext();
    const clearStyleGuide = useCallback(() => {
        setStyleGuide({cssClasses: defaultStyleGuide, cssClassMeta: {}});
        remove();
    }, [remove, setStyleGuide]);

    const onClickSave = useCallback(async () => {
        setSaving(true);
        try {
            if (value) {
                const updatedProject = await updateStyleGuide(userId, value);
                setProject(updatedProject);
            }
        } catch (error) {
            setToast({
                message: 'Something went wrong updating styleguide',
                type: ERROR,
                open: true,
            });
        } finally {
            setSaving(false);
        }
    }, [setToast, setProject, userId, value]);

    const debouncedOnChangePreviewText = useDebouncedCallback(
        (e) => {
            setPreviewText(e.target.value);
        },
        [],
        1000
    );

    const onToggleShowPreviewText = useCallback(
        (checked: boolean) => {
            setShowPreviewText(checked);
        },
        [setShowPreviewText]
    );

    return (
        <div className="flex w-full flex-col items-start justify-start gap-4">
            <h1 className="text-3xl font-black text-neutral-700">Rendition Styleguide</h1>
            <div className="flex w-full flex-row items-center justify-between gap-4">
                <div className="flex flex-row gap-4">
                    {clipboardEnabled && (
                        <Button className="basis-auto text-neutral-800" onClick={pasteFromClipboard} outlined>
                            Paste From Clipboard
                        </Button>
                    )}
                </div>
                <div className="flex flex-row gap-4">
                    <Checkbox
                        className="whitespace-nowrap text-neutral-800"
                        label={'Show Preview'}
                        onChange={onToggleShowPreviewText}
                        checked={showPreviewText}
                    />
                    <div className="flex w-32 basis-full items-stretch justify-center">
                        <Input
                            inputProps={{
                                onChange: debouncedOnChangePreviewText,
                                className: 'pl-2 pr-2 h-full text-neutral-700 placeholder:text-neutral-600',
                                placeholder: previewText,
                            }}
                        />
                    </div>
                    <Button
                        className="basis-auto text-neutral-700"
                        success
                        isLoading={saving}
                        onClick={onClickSave}
                        outlined
                    >
                        Save
                    </Button>
                    <Button error={true} className="basis-auto text-neutral-700" onClick={clearStyleGuide} outlined>
                        Clear Styleguide
                    </Button>
                </div>
            </div>
        </div>
    );
};
